import { ElementRef } from '@angular/core';
import {
  CropOptions as CanvasCropOptions,
  CropUnit as CanvasCropUnit,
  DEFAULT_CROP_PRESENTATION,
  EditorArrangeTileInput,
  EditorCanvas,
  EditorCanvasCropController,
  EditorRasterImage,
} from '@imagise/entities';
import { PictureUnit } from '../../domain/enums/editor.enum';
import { Rectangle, Size2D } from '../../domain/interfaces/shared';
import {
  applyCommittedEdits,
  applyDraftPreview,
  EditorPreviewableDraft,
  toPreviewableDraft,
} from './apply-edits';
import { createCanvas } from './canvas-dom.utils';
import {
  EditorCanvasViewState,
  EditorCommittedEdit,
  cloneCommittedEdits,
  cloneDraftOperation,
  isCropDraftOperation,
} from '../../domain';

const MIN_FILTER_PREVIEW_MAX_DIMENSION = 960;
const FILTER_PREVIEW_VIEWPORT_SCALE = 1.5;
// Keeps decoded animation frames warm: the frame player re-sets the same data
// URLs every loop and re-decoding them each cycle would stutter short frames.
const RASTER_IMAGE_CACHE_LIMIT = 12;

const NORMALIZED_DETECTION_MAX = 1000;

export interface ObjectDetectionBox {
  label: string;
  /** Axis-aligned box as x, y, width, height in normalized 0..1000 image coordinates. */
  bounds: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

export function isNormalizedDetectionBounds(bounds: ObjectDetectionBox['bounds']): boolean {
  const { x, y, width, height } = bounds;
  const x2 = x + width;
  const y2 = y + height;
  return (
    [x, y, x2, y2].every(value => Number.isFinite(value) && value >= 0 && value <= NORMALIZED_DETECTION_MAX) &&
    width > 0 &&
    height > 0
  );
}

export function toObjectDetectionBox(object: {
  label: string;
  bounds?: { x?: number; y?: number; width?: number; height?: number } | null;
}): ObjectDetectionBox | null {
  const x = object.bounds?.x;
  const y = object.bounds?.y;
  const width = object.bounds?.width;
  const height = object.bounds?.height;
  if (x == null || y == null || width == null || height == null) {
    return null;
  }

  const bounds = { x, y, width, height };
  if (!isNormalizedDetectionBounds(bounds)) {
    return null;
  }

  return { label: object.label, bounds };
}

export class EditorCanvasRenderer {
  constructor(parent: ElementRef<HTMLElement>) {
    this.root = parent.nativeElement;
    this.document = this.root.ownerDocument;
    this.editorCanvas = new EditorCanvas(undefined, {
      width: this.parentWidth,
      height: this.parentHeight,
      renderOnAddRemove: false,
      selection: false,
      skipTargetFind: true,
    });
    this.cropController = new EditorCanvasCropController(this.editorCanvas, {
      presentation: DEFAULT_CROP_PRESENTATION,
    });
    this.cropController.onRectChanged = rect => {
      this.onCropRectChanged?.(
        rect
          ? {
              x: rect.x,
              y: rect.y,
              width: rect.width,
              height: rect.height,
            }
          : null
      );
    };
    this.editorCanvas.onArrangeTileMoved = (id, x, y) => {
      this.onArrangeTileMoved?.(id, Math.round(x), Math.round(y));
    };
    this.root.addEventListener('wheel', this.handleArrangeWheel, { passive: false });
    this.initializeCanvasElements();
    this.detectionOverlay = this.document.createElement('canvas');
    this.detectionOverlay.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:1';
    this.detectionOverlay.hidden = true;
    this.detectionOverlay.setAttribute('aria-hidden', 'true');
    this.root.append(this.detectionOverlay);
    this.detachDetectionRender = this.editorCanvas.on('after:render', () => this.renderDetectionOverlay());
  }

  imageLoadedCallback?: () => void;
  onCropRectChanged?: (rect: Rectangle | null) => void;
  onArrangeTileMoved?: (id: string, x: number, y: number) => void;

  private readonly root: HTMLElement;
  private readonly document: Document;
  private readonly editorCanvas: EditorCanvas;
  private readonly cropController: EditorCanvasCropController;
  private readonly detectionOverlay: HTMLCanvasElement;
  private readonly detachDetectionRender: () => void;
  private detectionBoxes: readonly ObjectDetectionBox[] = [];
  private arrangeActive = false;
  private arrangeFitKey: string | null = null;
  private arrangeArea: Size2D | null = null;
  private arrangeManualZoom = false;

  private parentWidth = 640;
  private parentHeight = 480;

  private sourceImage: EditorRasterImage | null = null;
  private loadedImageUrl: string | null = null;
  private imageLoadVersion = 0;
  private imageLoading = false;
  private readonly rasterImageCache = new Map<string, EditorRasterImage>();
  private viewState: EditorCanvasViewState = {
    previewUrl: null,
    committedEdits: [],
    draft: null,
  };

  private pendingRenderHandle: number | ReturnType<typeof setTimeout> | null = null;
  private pendingRenderMode: 'raf' | 'timeout' | null = null;
  private committedCanvasKey: string | null = null;
  private previewCanvasKey: string | null = null;
  private filterPreviewSourceKey: string | null = null;
  private filterPreviewSourceCanvas: HTMLCanvasElement | null = null;
  private appliedCanvas: HTMLCanvasElement | null = null;
  private renderedCanvas: HTMLCanvasElement | null = null;

  get imageSize(): Size2D | null {
    const imageSize = this.editorCanvas.getCurrentImageSize();
    if (!imageSize) {
      return null;
    }

    return {
      width: imageSize.width,
      height: imageSize.height,
    };
  }

  set imageUrl(value: string) {
    if (!value || value === this.loadedImageUrl) {
      return;
    }

    this.loadedImageUrl = value;
    this.loadImage(value);
  }

  setViewState(viewState: EditorCanvasViewState): void {
    const nextViewState = {
      previewUrl: viewState.previewUrl,
      committedEdits: cloneCommittedEdits(viewState.committedEdits),
      draft: cloneDraftOperation(viewState.draft),
    };
    const shouldRenderCanvas = this.shouldRenderCanvas(nextViewState);
    if (
      nextViewState.previewUrl !== this.viewState.previewUrl ||
      this.serializeCommittedEdits(nextViewState.committedEdits) !==
        this.serializeCommittedEdits(this.viewState.committedEdits)
    ) {
      this.setObjectDetectionBoxes([]);
    }

    this.viewState = nextViewState;

    if (!shouldRenderCanvas) {
      this.syncCropOverlay();
      this.renderDetectionOverlay();
      return;
    }

    this.requestRender();
  }

  setObjectDetectionBoxes(boxes: readonly ObjectDetectionBox[]): void {
    this.detectionBoxes = boxes;
    this.renderDetectionOverlay();
  }

  get canExportCommittedPng(): boolean {
    return this.sourceImage !== null && !this.imageLoading && !this.arrangeActive;
  }

  exportCommittedPngBase64(): string | null {
    if (!this.canExportCommittedPng) {
      return null;
    }

    try {
      // Flush queued edits so a click before the next animation frame cannot export old pixels.
      this.render();
      const dataUrl = this.appliedCanvas?.toDataURL('image/png');
      const prefix = 'data:image/png;base64,';
      return dataUrl?.startsWith(prefix) ? dataUrl.slice(prefix.length) || null : null;
    } catch {
      return null;
    }
  }

  exportToSvg(): string {
    const canvas = this.appliedCanvas ?? this.renderedCanvas;
    if (!canvas) {
      return '';
    }

    try {
      const dataUrl = canvas.toDataURL('image/png');
      return [
        `<svg xmlns="http://www.w3.org/2000/svg" width="${canvas.width}" height="${canvas.height}" viewBox="0 0 ${canvas.width} ${canvas.height}">`,
        `<image href="${dataUrl}" width="${canvas.width}" height="${canvas.height}" />`,
        '</svg>',
      ].join('');
    } catch {
      return '';
    }
  }

  resize(width: number, height: number): void {
    this.parentWidth = Math.max(1, Math.floor(width));
    this.parentHeight = Math.max(1, Math.floor(height));

    this.editorCanvas.setDimensions({
      width: this.parentWidth,
      height: this.parentHeight,
    });

    if (this.arrangeActive && this.arrangeArea) {
      if (!this.arrangeManualZoom) {
        this.editorCanvas.fitAreaToViewport(this.arrangeArea, {
          padding: DEFAULT_CROP_PRESENTATION.previewPadding,
          maxScale: 1,
          align: 'start',
        });
      }
      this.editorCanvas.cancelRenderAll();
      this.editorCanvas.renderAll();
    } else if (this.sourceImage) {
      this.editorCanvas.fitImageToViewport({
        padding: DEFAULT_CROP_PRESENTATION.previewPadding,
        maxScale: 1,
      });
      this.editorCanvas.cancelRenderAll();
      this.editorCanvas.renderAll();
    } else {
      this.cropController.render();
    }
  }

  /**
   * Replaces the regular view with freely draggable tiles (or restores the
   * regular view when called with null). The viewport is refit only when the
   * tile set changes — positional updates must not yank the camera mid-drag.
   */
  setArrangeTiles(tiles: readonly EditorArrangeTileInput[] | null): void {
    if (tiles && tiles.length) {
      // Order-independent: reordering only changes the z-order and must not
      // yank the camera. A manual wheel zoom sticks until arrange mode ends.
      const fitKey = tiles.map(tile => tile.id).sort().join('|');
      const shouldRefit =
        !this.arrangeManualZoom && (!this.arrangeActive || fitKey !== this.arrangeFitKey);

      this.cancelScheduledRender();
      this.cropController.reset();
      this.editorCanvas.skipTargetFind = false;
      this.editorCanvas.getRasterLayer()?.set('visible', false);
      this.editorCanvas.setArrangeTiles(tiles);
      this.arrangeActive = true;
      this.arrangeFitKey = fitKey;
      this.arrangeArea = computeArrangeArea(tiles);

      if (shouldRefit) {
        this.editorCanvas.fitAreaToViewport(this.arrangeArea, {
          padding: DEFAULT_CROP_PRESENTATION.previewPadding,
          maxScale: 1,
          align: 'start',
        });
      }

      this.editorCanvas.cancelRenderAll();
      this.editorCanvas.renderAll();
      return;
    }

    if (!this.arrangeActive) {
      return;
    }

    this.arrangeActive = false;
    this.arrangeFitKey = null;
    this.arrangeArea = null;
    this.arrangeManualZoom = false;
    this.editorCanvas.clearArrangeTiles();
    this.editorCanvas.skipTargetFind = true;
    this.editorCanvas.getRasterLayer()?.set('visible', true);
    this.requestRender();
  }

  render(): void {
    this.cancelScheduledRender();

    if (this.arrangeActive) {
      return;
    }

    if (!this.sourceImage) {
      this.clearVisibleCanvases();
      return;
    }

    const rasterLayer = this.editorCanvas.getRasterLayer();
    if (!rasterLayer) {
      return;
    }

    const committedCanvasKey = this.serializeCommittedEdits(this.viewState.committedEdits);
    const appliedCanvas = this.resolveAppliedCanvas(committedCanvasKey);
    const previewDraft = toPreviewableDraft(this.viewState.draft);
    const previewCanvasKey = this.serializePreviewState(committedCanvasKey, this.viewState.draft);
    const previewCanvas = this.resolvePreviewCanvas(
      appliedCanvas,
      previewDraft,
      committedCanvasKey,
      previewCanvasKey
    );

    if (previewCanvas === appliedCanvas) {
      this.previewCanvasKey = previewCanvasKey;
    }

    this.appliedCanvas = appliedCanvas;
    this.renderedCanvas = previewCanvas;

    rasterLayer.replaceAppliedSurface(appliedCanvas, {
      clearDraft: true,
    });

    if (previewCanvas !== appliedCanvas) {
      rasterLayer.setDraftSurface(previewCanvas);
    }

    this.editorCanvas.fitImageToViewport({
      padding: DEFAULT_CROP_PRESENTATION.previewPadding,
      maxScale: 1,
    });
    this.syncCropOverlay();
    this.editorCanvas.cancelRenderAll();
    this.editorCanvas.renderAll();
  }

  dispose(): void {
    this.cancelScheduledRender();
    ++this.imageLoadVersion;
    this.root.removeEventListener('wheel', this.handleArrangeWheel);
    this.detachDetectionRender();
    this.detectionOverlay.remove();
    this.cropController.dispose();
    this.editorCanvas.dispose();
    this.rasterImageCache.clear();
    this.sourceImage = null;
    this.committedCanvasKey = null;
    this.previewCanvasKey = null;
    this.filterPreviewSourceKey = null;
    this.filterPreviewSourceCanvas = null;
    this.appliedCanvas = null;
    this.renderedCanvas = null;
    this.viewState = {
      previewUrl: null,
      committedEdits: [],
      draft: null,
    };
  }

  /**
   * Wheel zoom of the arrange view, anchored at the cursor. The user controls
   * the scale from here on: automatic refits stay off until arrange mode ends.
   */
  private readonly handleArrangeWheel = (event: WheelEvent): void => {
    if (!this.arrangeActive) {
      return;
    }

    event.preventDefault();

    const transform = this.editorCanvas.viewportTransform;
    const currentScale = transform[0] || 1;
    const nextScale = Math.min(4, Math.max(0.05, currentScale * (event.deltaY < 0 ? 1.1 : 1 / 1.1)));
    if (nextScale === currentScale) {
      return;
    }

    const rect = this.root.getBoundingClientRect();
    const pointerX = event.clientX - rect.left;
    const pointerY = event.clientY - rect.top;
    const ratio = nextScale / currentScale;

    this.editorCanvas.setViewportTransform([
      nextScale,
      0,
      0,
      nextScale,
      pointerX - (pointerX - transform[4]) * ratio,
      pointerY - (pointerY - transform[5]) * ratio,
    ]);
    this.arrangeManualZoom = true;
    this.editorCanvas.cancelRenderAll();
    this.editorCanvas.renderAll();
  };

  private initializeCanvasElements(): void {
    this.root.innerHTML = '';
    this.root.style.position = 'relative';
    this.root.style.overflow = 'hidden';
    this.applyCheckerboardBackground();

    const wrapper = this.editorCanvas.wrapperEl;
    if (wrapper) {
      wrapper.style.position = 'absolute';
      wrapper.style.left = '0';
      wrapper.style.top = '0';
      wrapper.style.width = '100%';
      wrapper.style.height = '100%';
    }

    const lowerCanvas = this.editorCanvas.lowerCanvasEl;
    if (lowerCanvas) {
      lowerCanvas.style.background = 'transparent';
    }
    const upperCanvas = this.editorCanvas.upperCanvasEl;
    if (upperCanvas) {
      upperCanvas.style.background = 'transparent';
    }

    if (wrapper) {
      this.root.append(wrapper);
    }
  }

  private loadImage(url: string): void {
    const loadVersion = ++this.imageLoadVersion;
    this.imageLoading = true;
    this.setObjectDetectionBoxes([]);

    this.resolveRasterImage(url)
      .then(image => {
        if (loadVersion !== this.imageLoadVersion) {
          return;
        }

        this.sourceImage = image;
        this.imageLoading = false;
        this.invalidateRenderCache();
        this.editorCanvas.loadRasterImage(image);
        // loadRasterImage creates a fresh (visible) layer — arrange mode must
        // keep the flat preview hidden behind the live tiles.
        if (this.arrangeActive) {
          this.editorCanvas.getRasterLayer()?.set('visible', false);
        }
        this.render();
        this.imageLoadedCallback?.();
      })
      .catch(() => {
        if (loadVersion !== this.imageLoadVersion) {
          return;
        }

        this.sourceImage = null;
        this.imageLoading = false;
        this.invalidateRenderCache();
        this.cropController.reset();
        this.clearVisibleCanvases();
      });
  }

  private renderDetectionOverlay(): void {
    const overlay = this.detectionOverlay;
    overlay.hidden = !this.detectionBoxes.length || this.imageLoading || this.arrangeActive || Boolean(this.viewState.draft);
    if (overlay.hidden) {
      return;
    }

    const pixelRatio = this.document.defaultView?.devicePixelRatio || 1;
    overlay.width = Math.round(this.parentWidth * pixelRatio);
    overlay.height = Math.round(this.parentHeight * pixelRatio);

    const imageSize = this.imageSize;
    const context = overlay.getContext('2d');
    if (!imageSize || !context) {
      return;
    }

    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    context.strokeStyle = '#22d3ee';
    context.lineWidth = 2;
    context.font = '600 13px sans-serif';
    context.textBaseline = 'top';

    for (const box of this.detectionBoxes) {
      if (!isNormalizedDetectionBounds(box.bounds)) {
        continue;
      }

      const { x, y, width, height } = box.bounds;
      const x2 = x + width;
      const y2 = y + height;

      const start = this.editorCanvas.imageToScreen({ x: x * imageSize.width / 1000, y: y * imageSize.height / 1000 });
      const end = this.editorCanvas.imageToScreen({ x: x2 * imageSize.width / 1000, y: y2 * imageSize.height / 1000 });
      if (!start || !end) {
        continue;
      }

      context.strokeRect(start.x, start.y, end.x - start.x, end.y - start.y);
      const labelWidth = Math.min(context.measureText(box.label).width + 8, this.parentWidth);
      const labelX = Math.max(0, Math.min(start.x, this.parentWidth - labelWidth));
      const labelY = Math.max(0, start.y - 21);
      context.fillStyle = '#083344';
      context.fillRect(labelX, labelY, labelWidth, 21);
      context.fillStyle = '#fff';
      context.fillText(box.label, labelX + 4, labelY + 4, Math.max(1, labelWidth - 8));
    }
  }

  private resolveRasterImage(url: string): Promise<EditorRasterImage> {
    const cached = this.rasterImageCache.get(url);
    if (cached) {
      // refresh the LRU position
      this.rasterImageCache.delete(url);
      this.rasterImageCache.set(url, cached);
      return Promise.resolve(cached);
    }

    return EditorRasterImage.fromSource(url).then(image => {
      this.rasterImageCache.set(url, image);
      while (this.rasterImageCache.size > RASTER_IMAGE_CACHE_LIMIT) {
        const oldestUrl = this.rasterImageCache.keys().next().value as string;
        this.rasterImageCache.delete(oldestUrl);
      }

      return image;
    });
  }

  private clearVisibleCanvases(): void {
    this.cancelScheduledRender();
    this.invalidateRenderCache();
    this.cropController.reset();
    this.editorCanvas.clearRasterLayer();
    this.editorCanvas.cancelRenderAll();
    this.editorCanvas.renderAll();
  }

  private shouldRenderCanvas(nextViewState: EditorCanvasViewState): boolean {
    if (nextViewState.previewUrl !== this.viewState.previewUrl) {
      return true;
    }

    if (
      this.serializeCommittedEdits(nextViewState.committedEdits) !==
      this.serializeCommittedEdits(this.viewState.committedEdits)
    ) {
      return true;
    }

    return !this.isCropOverlayOnlyTransition(this.viewState.draft, nextViewState.draft);
  }

  private isCropOverlayOnlyTransition(
    previousDraft: EditorCanvasViewState['draft'],
    nextDraft: EditorCanvasViewState['draft']
  ): boolean {
    return this.isCropOrEmptyDraft(previousDraft) && this.isCropOrEmptyDraft(nextDraft);
  }

  private isCropOrEmptyDraft(draft: EditorCanvasViewState['draft']): boolean {
    return !draft || draft.kind === 'crop';
  }

  private syncCropOverlay(): void {
    const cropDraft = isCropDraftOperation(this.viewState.draft) ? this.viewState.draft : null;

    if (!cropDraft) {
      this.cropController.reset();
      return;
    }

    this.cropController.setOptions(this.toCanvasCropOptions(cropDraft.params));
    this.cropController.setRect(cropDraft.params.rect ?? null);
  }

  private requestRender(): void {
    if (this.pendingRenderHandle !== null) {
      return;
    }

    if (typeof globalThis.requestAnimationFrame === 'function') {
      this.pendingRenderMode = 'raf';
      this.pendingRenderHandle = globalThis.requestAnimationFrame(() => {
        this.pendingRenderHandle = null;
        this.pendingRenderMode = null;
        this.render();
      });
      return;
    }

    this.pendingRenderMode = 'timeout';
    this.pendingRenderHandle = setTimeout(() => {
      this.pendingRenderHandle = null;
      this.pendingRenderMode = null;
      this.render();
    }, 16);
  }

  private cancelScheduledRender(): void {
    if (this.pendingRenderHandle === null) {
      return;
    }

    if (this.pendingRenderMode === 'raf' && typeof globalThis.cancelAnimationFrame === 'function') {
      globalThis.cancelAnimationFrame(this.pendingRenderHandle as number);
    } else if (this.pendingRenderMode === 'timeout') {
      clearTimeout(this.pendingRenderHandle as ReturnType<typeof setTimeout>);
    }

    this.pendingRenderHandle = null;
    this.pendingRenderMode = null;
  }

  private invalidateRenderCache(): void {
    this.committedCanvasKey = null;
    this.previewCanvasKey = null;
    this.filterPreviewSourceKey = null;
    this.filterPreviewSourceCanvas = null;
    this.appliedCanvas = null;
    this.renderedCanvas = null;
  }

  private resolveAppliedCanvas(committedCanvasKey: string): HTMLCanvasElement {
    if (this.appliedCanvas && this.committedCanvasKey === committedCanvasKey) {
      return this.appliedCanvas;
    }

    const sourceImage = this.sourceImage;
    if (!sourceImage) {
      throw new Error('resolveAppliedCanvas called before the source image is loaded');
    }

    const nextAppliedCanvas = applyCommittedEdits(
      this.document,
      sourceImage.snapshot(),
      this.viewState.committedEdits
    );

    this.committedCanvasKey = committedCanvasKey;
    this.previewCanvasKey = null;
    this.appliedCanvas = nextAppliedCanvas;

    return nextAppliedCanvas;
  }

  private resolvePreviewCanvas(
    appliedCanvas: HTMLCanvasElement,
    previewDraft: EditorPreviewableDraft | null,
    committedCanvasKey: string,
    previewCanvasKey: string
  ): HTMLCanvasElement {
    if (!previewDraft) {
      return appliedCanvas;
    }

    if (this.renderedCanvas && this.previewCanvasKey === previewCanvasKey) {
      return this.renderedCanvas;
    }

    const previewSourceCanvas = previewDraft.kind === 'filter'
      ? this.resolveFilterPreviewSourceCanvas(appliedCanvas, committedCanvasKey)
      : appliedCanvas;
    const nextPreviewCanvas = applyDraftPreview(this.document, previewSourceCanvas, previewDraft);
    this.previewCanvasKey = previewCanvasKey;
    this.renderedCanvas = nextPreviewCanvas;

    return nextPreviewCanvas;
  }

  private serializeCommittedEdits(edits: EditorCommittedEdit[]): string {
    return JSON.stringify(edits);
  }

  private serializePreviewState(committedCanvasKey: string, draft: EditorCanvasViewState['draft']): string {
    return `${committedCanvasKey}::${JSON.stringify(draft)}`;
  }

  private createFilterPreviewSourceCanvas(sourceCanvas: HTMLCanvasElement): HTMLCanvasElement {
    const maxDimension = Math.max(
      MIN_FILTER_PREVIEW_MAX_DIMENSION,
      Math.round(Math.max(this.parentWidth, this.parentHeight) * FILTER_PREVIEW_VIEWPORT_SCALE)
    );
    const sourceMaxDimension = Math.max(sourceCanvas.width, sourceCanvas.height);

    if (sourceMaxDimension <= maxDimension) {
      return sourceCanvas;
    }

    const scale = maxDimension / sourceMaxDimension;
    const targetWidth = Math.max(1, Math.round(sourceCanvas.width * scale));
    const targetHeight = Math.max(1, Math.round(sourceCanvas.height * scale));
    const targetCanvas = createCanvas(this.document, targetWidth, targetHeight);
    const targetContext = targetCanvas.getContext('2d');

    if (!targetContext) {
      return sourceCanvas;
    }

    targetContext.imageSmoothingEnabled = true;
    targetContext.drawImage(sourceCanvas, 0, 0, targetWidth, targetHeight);

    return targetCanvas;
  }

  private resolveFilterPreviewSourceCanvas(
    sourceCanvas: HTMLCanvasElement,
    committedCanvasKey: string
  ): HTMLCanvasElement {
    const maxDimension = Math.max(
      MIN_FILTER_PREVIEW_MAX_DIMENSION,
      Math.round(Math.max(this.parentWidth, this.parentHeight) * FILTER_PREVIEW_VIEWPORT_SCALE)
    );
    const cacheKey = `${committedCanvasKey}::${sourceCanvas.width}x${sourceCanvas.height}::${maxDimension}`;

    if (this.filterPreviewSourceCanvas && this.filterPreviewSourceKey === cacheKey) {
      return this.filterPreviewSourceCanvas;
    }

    const previewCanvas = this.createFilterPreviewSourceCanvas(sourceCanvas);
    this.filterPreviewSourceKey = cacheKey;
    this.filterPreviewSourceCanvas = previewCanvas;

    return previewCanvas;
  }

  private applyCheckerboardBackground(): void {
    const gridSize = `${DEFAULT_CROP_PRESENTATION.checkerboardSize * 2}px ${DEFAULT_CROP_PRESENTATION.checkerboardSize * 2}px`;
    this.root.style.backgroundColor = DEFAULT_CROP_PRESENTATION.checkerboardLight;
    this.root.style.backgroundImage = [
      `linear-gradient(45deg, ${DEFAULT_CROP_PRESENTATION.checkerboardDark} 25%, transparent 25%, transparent 75%, ${DEFAULT_CROP_PRESENTATION.checkerboardDark} 75%, ${DEFAULT_CROP_PRESENTATION.checkerboardDark})`,
      `linear-gradient(45deg, ${DEFAULT_CROP_PRESENTATION.checkerboardDark} 25%, transparent 25%, transparent 75%, ${DEFAULT_CROP_PRESENTATION.checkerboardDark} 75%, ${DEFAULT_CROP_PRESENTATION.checkerboardDark})`,
    ].join(', ');
    this.root.style.backgroundPosition = `0 0, ${DEFAULT_CROP_PRESENTATION.checkerboardSize}px ${DEFAULT_CROP_PRESENTATION.checkerboardSize}px`;
    this.root.style.backgroundSize = `${gridSize}, ${gridSize}`;
  }

  private toCanvasCropOptions(cropOptions: { width: number; height: number; units: PictureUnit }): CanvasCropOptions {
    return {
      width: cropOptions.width,
      height: cropOptions.height,
      units: this.toCanvasCropUnit(cropOptions.units),
    };
  }

  private toCanvasCropUnit(unit: PictureUnit): CanvasCropUnit {
    switch (unit) {
      case PictureUnit.Pixels:
        return CanvasCropUnit.Pixels;
      case PictureUnit.AspectRatio:
        return CanvasCropUnit.AspectRatio;
      case PictureUnit.Percentage:
      default:
        return CanvasCropUnit.Percentage;
    }
  }
}

function computeArrangeArea(tiles: readonly EditorArrangeTileInput[]): Size2D {
  let width = 1;
  let height = 1;

  for (const tile of tiles) {
    const image = tile.surface as HTMLImageElement;
    const tileWidth = Math.max(1, image.naturalWidth || tile.surface.width || 1);
    const tileHeight = Math.max(1, image.naturalHeight || tile.surface.height || 1);
    width = Math.max(width, tile.x + tileWidth);
    height = Math.max(height, tile.y + tileHeight);
  }

  return { width, height };
}
